import { hashString, mixSeed } from './hash';
import { Vec3 } from './Vec3';
import { TAU } from './scalar';

/**
 * Small, fast, fully deterministic PRNG (mulberry32).
 *
 * Used for content generation where a *stream* of values is natural (species
 * traits, settlement layout, loot rolls). Anything that must be reproducible
 * from a coordinate alone should use the hash functions instead, because a
 * stream depends on how many values were previously consumed.
 */
export class Rng {
  private state: number;

  constructor(seed: number | string) {
    this.state = (typeof seed === 'string' ? hashString(seed) : seed | 0) >>> 0;
    // Avoid the degenerate all-zero state.
    if (this.state === 0) this.state = 0x9e3779b9;
  }

  /** Derives an independent generator from this one without consuming it. */
  fork(tag: number | string): Rng {
    const t = typeof tag === 'string' ? hashString(tag) : tag;
    return new Rng(mixSeed(this.state, t));
  }

  /** Uniform in [0, 1). */
  next(): number {
    this.state = (this.state + 0x6d2b79f5) >>> 0;
    let t = this.state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** Uniform in [min, max). */
  range(min: number, max: number): number {
    return min + this.next() * (max - min);
  }

  /** Uniform integer in [min, max] inclusive. */
  int(min: number, max: number): number {
    return Math.floor(this.range(min, max + 1));
  }

  bool(pTrue = 0.5): boolean {
    return this.next() < pTrue;
  }

  pick<T>(items: readonly T[]): T {
    return items[Math.min(items.length - 1, Math.floor(this.next() * items.length))];
  }

  /** Picks an item using per-item weights. */
  weighted<T>(items: readonly T[], weights: readonly number[]): T {
    let total = 0;
    for (const w of weights) total += Math.max(0, w);
    if (total <= 0) return this.pick(items);
    let r = this.next() * total;
    for (let i = 0; i < items.length; i++) {
      r -= Math.max(0, weights[i]);
      if (r <= 0) return items[i];
    }
    return items[items.length - 1];
  }

  /** In-place Fisher-Yates shuffle. */
  shuffle<T>(items: T[]): T[] {
    for (let i = items.length - 1; i > 0; i--) {
      const j = Math.floor(this.next() * (i + 1));
      const tmp = items[i];
      items[i] = items[j];
      items[j] = tmp;
    }
    return items;
  }

  /** Standard normal via Box-Muller. */
  gaussian(mean = 0, stdDev = 1): number {
    const u1 = Math.max(1e-12, this.next());
    const u2 = this.next();
    return mean + stdDev * Math.sqrt(-2 * Math.log(u1)) * Math.cos(TAU * u2);
  }

  /** Uniformly distributed point on the unit sphere. */
  onSphere(): Vec3 {
    const z = this.range(-1, 1);
    const t = this.range(0, TAU);
    const r = Math.sqrt(Math.max(0, 1 - z * z));
    return new Vec3(r * Math.cos(t), r * Math.sin(t), z);
  }
}
